import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, UserIcon, EnvelopeIcon, PhoneIcon, BuildingOfficeIcon,
  CalendarIcon, ClockIcon, PencilIcon, ChatBubbleLeftRightIcon, UserGroupIcon,
  BanknotesIcon, TagIcon, CheckCircleIcon, ExclamationCircleIcon,
  ChevronDownIcon, ChevronUpIcon, XMarkIcon, ArrowPathIcon, TrashIcon
} from '@heroicons/react/24/outline';
import { leadsApi, authApi } from '../config/api';
import LeadsService from '../services/leadsService';
import LeadForm from './LeadForm';
import StatusChangeModal from './StatusChangeModal';
import FollowUpModal from './FollowUpModal';
import { useAuth } from '../context/AuthContext';

const LeadProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusModal, setStatusModal] = useState({ isOpen: false, viewHistoryOnly: false, newStatus: '' });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ contact: true, status: true, followups: true });
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, followUp: null, followUpIndex: -1 });
  const [rescheduleData, setRescheduleData] = useState({ newDate: '', reason: '', reasonType: '', notes: '' });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [expandedHistories, setExpandedHistories] = useState({}); // Track which follow-up histories are expanded
  const [scheduleFollowUpModal, setScheduleFollowUpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // WhatsApp template modal states
  const [whatsAppModal, setWhatsAppModal] = useState({
    isOpen: false,
    phone: '',
    lead: null
  });
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const getCompanyId = () => {
    const storedCompanyId = localStorage.getItem('companyId') || localStorage.getItem('selectedCompanyId');
    if (storedCompanyId) return storedCompanyId;

    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
      const cleanedToken = token.replace(/['"]/g, '').trim();
      const payload = JSON.parse(atob(cleanedToken.split('.')[1]));
      return payload.companyId || payload.company || payload.id;
    } catch (e) {
      console.error('Error decoding token for companyId:', e);
      return null;
    }
  };

  const defaultWhatsappTemplates = [
    {
      _id: 'default_1',
      title: 'Testing the lead sharing',
      location: 'Kolhapur, Maharashtra, India',
      message: '*Testing the lead sharing*\n\nTesting the lead sharing'
    },
    {
      _id: 'default_2',
      title: 'track',
      location: 'Maharashtra',
      message: '*track*\n\nYour lead is tracking'
    },
    {
      _id: 'default_3',
      title: 'vaibhav',
      location: 'Kolhapur, Maharashtra, India',
      message: '*vaibhav*\n\nvv'
    },
    {
      _id: 'default_4',
      title: 'test',
      location: 'Kolhapur, Maharashtra, India',
      message: '*test*'
    },
    {
      _id: 'default_5',
      title: 'Lead Follow-up',
      location: 'Office',
      message: 'Hello {name}, following up regarding your inquiry. Please let us know a suitable time to connect!'
    }
  ];

  const handleWhatsAppClick = async (phone, leadData) => {
    setWhatsAppModal({
      isOpen: true,
      phone: phone,
      lead: leadData
    });

    if (whatsappTemplates.length === 0) {
      try {
        setTemplatesLoading(true);
        const companyId = getCompanyId();

        if (companyId) {
          console.log('🔍 Fetching WhatsApp templates for company:', companyId);
          const response = await authApi.get(`/whatsapp-msg/company/${companyId}`);
          const fetched = response.data || [];
          if (fetched.length > 0) {
            setWhatsappTemplates(fetched);
          } else {
            setWhatsappTemplates(defaultWhatsappTemplates);
          }
        } else {
          setWhatsappTemplates(defaultWhatsappTemplates);
        }
      } catch (error) {
        console.error('❌ Error fetching WhatsApp templates:', error);
        setWhatsappTemplates(defaultWhatsappTemplates);
      } finally {
        setTemplatesLoading(false);
      }
    }
  };

  const resolveMessage = (message, leadData) => {
    if (!message) return '';
    let resolved = message;
    resolved = resolved.replace(/{name}/gi, leadData?.name || '');
    resolved = resolved.replace(/{leadName}/gi, leadData?.name || '');
    resolved = resolved.replace(/{phone}/gi, leadData?.phone || '');
    resolved = resolved.replace(/{email}/gi, leadData?.email || '');
    resolved = resolved.replace(/{budget}/gi, leadData?.budget || '');
    return resolved;
  };

  const sendWhatsAppMessage = (phone, message) => {
    let cleanedPhone = phone.toString().replace(/\D/g, '');
    if (cleanedPhone.length === 10) {
      cleanedPhone = '91' + cleanedPhone;
    }
    const url = `https://api.whatsapp.com/send?phone=${cleanedPhone}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Get current user - from context or localStorage (check multiple keys)
  const getCurrentUser = () => {
    if (user) return user;
    // Fallback to localStorage - check multiple possible keys
    try {
      // Try userInfo first (used by AuthContext)
      let userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        console.log('📦 Found userInfo in localStorage:', parsed);
        return parsed;
      }

      // Try userData (used by some components)
      userInfo = localStorage.getItem('userData');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        console.log('📦 Found userData in localStorage:', parsed);
        return parsed;
      }

      // Try to decode from token
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          // Decode JWT token (base64)
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('📦 Decoded token payload:', payload);
          // Also check for stored role/name
          const userRole = localStorage.getItem('userRole');
          const userId = localStorage.getItem('userId');

          // Extract name from email if not available
          let userName = payload.name;
          if (!userName && payload.email) {
            // Get name part from email (before @)
            userName = payload.email.split('@')[0];
            // Capitalize first letter
            userName = userName.charAt(0).toUpperCase() + userName.slice(1);
          }

          return {
            id: payload.id || userId,
            email: payload.email,
            role: payload.role || userRole,
            userType: payload.userType || userRole,
            name: userName
          };
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e);
    }
    return null;
  };

  const currentUserObj = getCurrentUser();
  const currentUserRole = (currentUserObj?.role || currentUserObj?.userType || '').toLowerCase();
  const canSeePhone = currentUserRole === 'company' ||
    currentUserRole === 'franchise' ||
    currentUserRole === 'manager' ||
    currentUserRole === 'admin' ||
    currentUserRole === 'superadmin' ||
    currentUserRole.includes('manager');

  // Get current user name - check all possible name fields
  // If company logged in, show company name, if employee show their name
  const getCurrentUserName = () => {
    const currentUser = getCurrentUser();
    console.log('👤 Current user object:', currentUser);
    console.log('👤 All localStorage keys:', Object.keys(localStorage));
    console.log('👤 userInfo:', localStorage.getItem('userInfo'));
    console.log('👤 userData:', localStorage.getItem('userData'));

    if (!currentUser) return 'Unknown';

    // Check if user is company - show company name
    const userRole = (currentUser.role || currentUser.userType || '').toLowerCase();
    console.log('👤 User role:', userRole);

    const isCompany = userRole === 'company' || userRole === 'franchise';

    if (isCompany) {
      // For company, show businessName or name
      const companyName = currentUser.businessName || currentUser.name;
      console.log('📝 Company user, name:', companyName);
      if (companyName && companyName !== 'Company') {
        return companyName;
      }
      // If no name, use email prefix
      if (currentUser.email) {
        const emailName = currentUser.email.split('@')[0];
        return emailName.charAt(0).toUpperCase() + emailName.slice(1) + ' (Company)';
      }
      return 'Company';
    }

    // For employees, show their name
    const name = currentUser.name || currentUser.teamMemberName ||
      currentUser.firstName || currentUser.fullName || currentUser.displayName;
    console.log('📝 Employee user, name:', name);

    if (name && name !== 'Employee') {
      return name;
    }

    // Fallback to email prefix
    if (currentUser.email) {
      const emailName = currentUser.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }

    return 'Employee';
  };

  // Get current user email
  const getCurrentUserEmail = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return '';
    return currentUser.email || currentUser.businessEmail || currentUser.teamMemberEmail || '';
  };

  useEffect(() => { fetchLead(); fetchLeadStatuses(); }, [id]);

  const fetchLead = async () => {
    try {
      setLoading(true);
      const response = await leadsApi.get(`/leads/${id}`);
      setLead(response.data);
    } catch (err) {
      setError('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadStatuses = async () => {
    try {
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) setLeadStatuses(response.data.statuses);
    } catch (err) { console.error('Error:', err); }
  };

  const handleStatusChange = async (remark) => {
    try {
      await leadsApi.put(`/leads/${id}`, { status: statusModal.newStatus, statusRemark: remark });
      fetchLead();
      setStatusModal({ isOpen: false, viewHistoryOnly: false, newStatus: '' });
    } catch (err) { console.error('Error:', err); }
  };

  const handleEditLead = async (leadData) => {
    try {
      const result = await LeadsService.updateLead(id, leadData);
      if (result.success) {
        fetchLead();
        setShowEditModal(false);
      } else {
        alert(`Failed to update lead: ${result.error}`);
      }
    } catch (err) {
      console.error('Error updating lead:', err);
      alert('Failed to update lead');
    }
  };

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const openRescheduleModal = (followUp, index) => {
    const currentDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;
    setRescheduleData({
      newDate: currentDate ? new Date(currentDate).toISOString().slice(0, 16) : '',
      reason: '',
      reasonType: '',
      notes: ''
    });
    setRescheduleModal({ isOpen: true, followUp, followUpIndex: index });
  };

  const handleReschedule = async () => {
    if (!rescheduleData.newDate) {
      alert('Please select a new date');
      return;
    }

    setRescheduleLoading(true);
    try {
      const followUp = rescheduleModal.followUp;
      const followUpId = followUp._id;
      const previousDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;

      const userName = getCurrentUserName();
      const userEmail = getCurrentUserEmail();
      console.log('📝 Rescheduling by user:', userName, 'email:', userEmail);

      // Build reschedule history entry with user info
      const newHistoryEntry = {
        previousDate: previousDate,
        newDate: new Date(rescheduleData.newDate).toISOString(),
        reason: rescheduleData.reason || 'Not specified',
        notes: rescheduleData.notes || '',
        rescheduledAt: new Date().toISOString(),
        rescheduledBy: userName,
        rescheduledByEmail: userEmail
      };

      console.log('📝 New history entry:', newHistoryEntry);

      // Get existing history or create new array
      const existingHistory = followUp.rescheduleHistory || [];
      const updatedHistory = [...existingHistory, newHistoryEntry];

      await leadsApi.put(`/follow-up/update/${id}/${followUpId}`, {
        scheduledDate: new Date(rescheduleData.newDate).toISOString(),
        nextFollowUpDate: new Date(rescheduleData.newDate).toISOString(),
        rescheduled: true,
        rescheduledAt: new Date().toISOString(),
        rescheduledFrom: previousDate,
        rescheduleHistory: updatedHistory
      });

      fetchLead();
      setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 });
      setRescheduleData({ newDate: '', reason: '', reasonType: '', notes: '' });
    } catch (err) {
      console.error('Error rescheduling:', err);
      alert('Failed to reschedule follow-up');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleMarkComplete = async (followUp) => {
    try {
      await leadsApi.put(`/follow-up/update/${id}/${followUp._id}`, {
        completed: true,
        completedAt: new Date().toISOString(),
        status: 'completed',
        completedBy: getCurrentUserName(),
        completedByEmail: getCurrentUserEmail()
      });
      fetchLead();
    } catch (err) {
      console.error('Error marking complete:', err);
    }
  };

  const handleDeleteFollowUp = async (followUp) => {
    if (!window.confirm('Are you sure you want to delete this follow-up? This action cannot be undone.')) {
      return;
    }
    try {
      await leadsApi.delete(`/follow-up/delete/${id}/${followUp._id}`);
      fetchLead();
    } catch (err) {
      console.error('Error deleting follow-up:', err);
      alert('Failed to delete follow-up');
    }
  };

  const getStatusColor = (status) => {
    const c = {
      'new': { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-800' },
      'contacted': { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-800' },
      'qualified': { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-800' },
      'quotated': { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-800' },
      'won': { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-800' },
      'lost': { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-800' }
    };
    return c[status?.toLowerCase()] || c['new'];
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const formatShortDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const getFollowUpIcon = (t) => ({ 'call': '📞', 'email': '📧', 'meeting': '🤝', 'demo': '💻', 'proposal': '📄', 'message': '💬', 'assignment': '👤' }[t] || '📌');

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
    </div>
  );

  if (error || !lead) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-xl shadow">
        <p className="text-red-600 mb-4">{error || 'Lead not found'}</p>
        <button onClick={() => navigate('/lead-mng')} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Back</button>
      </div>
    </div>
  );

  const sc = getStatusColor(lead.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate('/lead-mng')} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 mt-1">
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg md:text-2xl font-bold text-gray-900 leading-tight">{lead.name}</h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sc.light} ${sc.text}`}>{lead.status || 'new'}</span>
              </div>
              {(lead.customFields?.title || lead.title) && (
                <p className="text-blue-600 text-sm font-medium">🎯 {lead.customFields?.title || lead.title}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5 md:gap-2 pl-0 md:pl-0 mt-2 md:mt-0 w-full md:w-auto">
            <button
              onClick={() => setScheduleFollowUpModal(true)}
              className="col-span-1 px-1.5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm"
            >
              <CalendarIcon className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span>Change Status<br className="md:hidden" />& Follow-up</span>
            </button>
            <button onClick={() => setStatusModal({ isOpen: true, viewHistoryOnly: true, newStatus: lead.status })} className="col-span-1 px-1.5 py-2 border rounded-lg hover:bg-gray-50 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm bg-white">
              <ClockIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
              <span>History</span>
            </button>
            <button onClick={() => setShowEditModal(true)} className="col-span-1 px-1.5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm">
              <PencilIcon className="h-4 w-4 md:h-5 md:w-5" />
              <span>Edit</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 md:px-4 py-3 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-3 md:space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Status</p>
                <p className={`text-xs md:text-sm font-bold truncate leading-none ${sc.text}`}>{lead.status || 'New'}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Follow-ups</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.followUps?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Changes</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.statusHistory?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Assigned</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.assignedEmployees?.length || 0}</p>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-lg md:rounded-xl shadow-sm border">
              <button onClick={() => toggleSection('contact')} className="w-full px-4 md:px-6 py-3 md:py-4 flex justify-between items-center bg-blue-50 border-b rounded-t-lg md:rounded-t-xl">
                <h2 className="font-semibold flex items-center gap-2 text-sm md:text-base"><UserIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" /> Contact Info</h2>
                {expandedSections.contact ? <ChevronUpIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ChevronDownIcon className="h-4 w-4 md:h-5 md:w-5" />}
              </button>
              {expandedSections.contact && (
                <div className="p-3 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-4">
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl hover:bg-blue-50 min-w-0">
                    <EnvelopeIcon className="h-5 w-5 md:h-6 md:w-6 text-blue-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] md:text-xs text-gray-500 font-medium">Email</p>
                      <p className="text-blue-600 font-medium text-xs md:text-sm truncate">{lead.email}</p>
                    </div>
                  </a>
                  {lead.phone && (
                    canSeePhone ? (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl hover:bg-green-50 min-w-0">
                        <PhoneIcon className="h-5 w-5 md:h-6 md:w-6 text-green-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 font-medium">Phone</p>
                          <p className="text-green-600 font-medium text-xs md:text-sm truncate">{lead.phone}</p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0 cursor-not-allowed" title="Only manager and company can view full contact number">
                        <PhoneIcon className="h-5 w-5 md:h-6 md:w-6 text-gray-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 font-medium">Phone</p>
                          <p className="text-gray-400 font-medium text-xs md:text-sm truncate">
                            {lead.phone.toString().trim().length > 4
                              ? '*'.repeat(lead.phone.toString().trim().length - 4) + lead.phone.toString().trim().slice(-4)
                              : '******'}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                  {lead.phone && (
                    <button
                      type="button"
                      onClick={() => handleWhatsAppClick(lead.phone, lead)}
                      className="flex items-center gap-2 md:gap-3 p-3 bg-emerald-50/80 rounded-lg md:rounded-xl hover:bg-emerald-100/90 transition-colors min-w-0 text-left border border-emerald-100/80 shadow-2xs group"
                      title="Send WhatsApp Template Message"
                    >
                      <svg className="w-5 h-5 md:w-6 md:h-6 fill-current text-emerald-600 shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.37 5.028L2 22l5.13-1.346a9.921 9.921 0 004.877 1.28h.005c5.505 0 9.99-4.478 9.99-9.985 0-2.67-1.037-5.18-2.92-7.065C17.198 3.003 14.685 2 12.012 2zm5.776 14.15c-.25.703-1.25 1.295-1.745 1.393-.454.09-1.042.164-3.037-.66-2.55-1.054-4.19-3.655-4.316-3.823-.127-.167-1.026-1.366-1.026-2.607a2.76 2.76 0 01.854-1.964c.264-.265.576-.33.776-.33.197-.002.395-.002.567.006.18.008.419-.068.653.486.242.576.827 2.018.898 2.163.072.146.12.316.023.51-.097.195-.145.316-.29.486-.145.17-.305.378-.435.508-.145.144-.298.302-.128.594.17.29.753 1.24 1.614 2.007.957.854 1.764 1.118 2.014 1.244.25.126.395.106.543-.066.149-.17.638-.744.808-1.013.17-.27.34-.226.574-.14.234.086 1.488.702 1.743.829.255.127.425.19.489.3.064.11.064.636-.186 1.34z"/>
                      </svg>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] md:text-xs text-gray-500 font-medium">WhatsApp</p>
                          <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-medium">Template</span>
                        </div>
                        <p className="text-emerald-700 font-semibold text-xs md:text-sm truncate">
                          {canSeePhone ? lead.phone : (lead.phone.toString().trim().length > 4 ? '*'.repeat(lead.phone.toString().trim().length - 4) + lead.phone.toString().trim().slice(-4) : '******')}
                        </p>
                      </div>
                    </button>
                  )}
                  {lead.company && (
                    <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0">
                      <BuildingOfficeIcon className="h-5 w-5 md:h-6 md:w-6 text-purple-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] md:text-xs text-gray-500 font-medium">Company</p>
                        <p className="font-medium text-xs md:text-sm truncate">{lead.company}</p>
                      </div>
                    </div>
                  )}
                  {lead.source && (
                    <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0">
                      <TagIcon className="h-5 w-5 md:h-6 md:w-6 text-orange-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] md:text-xs text-gray-500 font-medium">Source</p>
                        <p className="font-medium text-xs md:text-sm capitalize truncate">{lead.source}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Follow-ups */}
            <div className="bg-white rounded-lg md:rounded-xl shadow-sm border">
              <button onClick={() => toggleSection('followups')} className="w-full px-4 md:px-6 py-3 md:py-4 flex justify-between items-center bg-blue-50 border-b rounded-t-lg md:rounded-t-xl">
                <h2 className="font-semibold flex items-center gap-2 text-sm md:text-base">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" /> Follow-ups
                  <span className="ml-1 md:ml-2 px-1.5 md:px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] md:text-xs rounded-full">{lead.followUps?.length || 0}</span>
                </h2>
                {expandedSections.followups ? <ChevronUpIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ChevronDownIcon className="h-4 w-4 md:h-5 md:w-5" />}
              </button>
              {expandedSections.followups && (
                <div className="p-6">
                  {lead.followUps?.length > 0 ? (
                    <div className="space-y-4">
                      {lead.followUps.slice().reverse().map((f, idx) => {
                        const actualIndex = lead.followUps.length - 1 - idx;
                        return (
                          <div key={idx} className={`p-4 rounded-xl border-2 ${f.completed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{getFollowUpIcon(f.type)}</span>
                                <span className="font-semibold capitalize">{f.type}</span>
                                {f.rescheduled && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Rescheduled</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${f.completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {f.completed ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationCircleIcon className="h-4 w-4" />}
                                  {f.completed ? 'Done' : 'Pending'}
                                </span>
                              </div>
                            </div>
                            <p className="font-medium text-gray-900">{f.summary}</p>
                            {f.notes && <p className="text-sm text-gray-600 mt-2 bg-white/50 p-2 rounded">{f.notes}</p>}

                            {/* Info Row */}
                            <div className="flex items-center flex-wrap gap-3 mt-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3.5 w-3.5" /> Created: {formatDate(f.date)}
                              </span>
                              {f.addedByName && (
                                <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                                  <UserIcon className="h-3 w-3" /> Added by: <span className="font-medium text-gray-700">{f.addedByName}</span>
                                </span>
                              )}
                              {(f.nextFollowUpDate || f.scheduledDate) && (
                                <span className="text-blue-600 font-medium flex items-center gap-1">
                                  📅 Scheduled: {formatDate(f.nextFollowUpDate || f.scheduledDate)}
                                </span>
                              )}
                            </div>

                            {/* Completed Info */}
                            {f.completed && (
                              <div className="mt-2 p-2 bg-green-100 rounded-lg">
                                <p className="text-xs text-green-800 flex items-center gap-2">
                                  <CheckCircleIcon className="h-4 w-4" />
                                  <span>Completed on {formatDate(f.completedAt)}</span>
                                  {f.completedBy && <span className="font-medium">by {f.completedBy}</span>}
                                </p>
                              </div>
                            )}

                            {/* Action Buttons */}
                            {!f.completed && (
                              <div className="flex flex-wrap items-center gap-2 mt-3">
                                <button
                                  onClick={() => openRescheduleModal(f, actualIndex)}
                                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 flex items-center gap-1"
                                >
                                  <ArrowPathIcon className="h-3.5 w-3.5" /> Reschedule
                                </button>
                                <button
                                  onClick={() => handleMarkComplete(f)}
                                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 flex items-center gap-1"
                                >
                                  <CheckCircleIcon className="h-3.5 w-3.5" /> Complete
                                </button>
                                <button
                                  onClick={() => handleDeleteFollowUp(f)}
                                  className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                                </button>
                              </div>
                            )}

                            {/* Delete button for completed follow-ups */}
                            {f.completed && (
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  onClick={() => handleDeleteFollowUp(f)}
                                  className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                                </button>
                              </div>
                            )}

                            {/* Reschedule History - Collapsible */}
                            {f.rescheduleHistory?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <button
                                  onClick={() => setExpandedHistories(prev => ({ ...prev, [f._id]: !prev[f._id] }))}
                                  className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  <ArrowPathIcon className="h-4 w-4" />
                                  <span>{expandedHistories[f._id] ? 'Hide' : 'Show'} Reschedule History ({f.rescheduleHistory.length})</span>
                                  {expandedHistories[f._id] ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                                </button>

                                {expandedHistories[f._id] && (
                                  <div className="mt-3 space-y-2 pl-2 border-l-2 border-blue-200">
                                    {f.rescheduleHistory.map((rh, rhIdx) => (
                                      <div key={rhIdx} className="relative pl-4">
                                        <div className="absolute -left-[5px] top-2 w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                          <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-600">{formatShortDate(rh.previousDate)}</span>
                                            <span className="text-blue-600 font-bold">→</span>
                                            <span className="text-blue-700 font-medium">{formatShortDate(rh.newDate)}</span>
                                          </div>
                                          {rh.reason && (
                                            <p className="text-xs text-gray-600 mt-1">
                                              <span className="font-medium">Reason:</span> {rh.reason}
                                            </p>
                                          )}
                                          {rh.notes && (
                                            <p className="text-xs text-gray-500 mt-1 italic">"{rh.notes}"</p>
                                          )}
                                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                            <span>{formatDate(rh.rescheduledAt)}</span>
                                            {rh.rescheduledBy && (
                                              <span className="bg-blue-100 px-2 py-0.5 rounded text-blue-700">
                                                by {rh.rescheduledBy}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No follow-ups yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {lead.notes && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="font-semibold mb-3">📝 Notes</h2>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-xl whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">


            {/* Assigned */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><UserGroupIcon className="h-4 w-4" /> Assigned To</h3>
                {lead.assignedEmployees?.length > 0 ? (
                  <div className="space-y-2">
                    {lead.assignedEmployees.map((emp, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                        <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">{(emp.name || 'E')[0]}</div>
                        <div>
                          <p className="font-semibold">{emp.name}</p>
                          {emp.email && <p className="text-xs text-gray-500">{emp.email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : lead.assignedEmployeeName ? (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">{lead.assignedEmployeeName[0]}</div>
                    <p className="font-semibold">{lead.assignedEmployeeName}</p>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl text-gray-500 text-sm">Not assigned</div>
                )}
              </div>
            </div>

            {/* Value */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><BanknotesIcon className="h-4 w-4" /> Value</h3>
                {lead.value > 0 && <div className="flex justify-between p-3 bg-blue-50 rounded-xl mb-2"><span>Value</span><span className="font-bold text-blue-600">₹{lead.value.toLocaleString()}</span></div>}
                {lead.budget && <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Budget</span><span className="font-bold text-blue-600">{lead.budget}</span></div>}
                {!lead.value && !lead.budget && <div className="text-center py-6 bg-gray-50 rounded-xl text-gray-500 text-sm">No value set</div>}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><CalendarIcon className="h-4 w-4" /> Timeline</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Created</span><span className="font-medium">{formatShortDate(lead.createdAt)}</span></div>
                  <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Last Contact</span><span className="font-medium">{formatShortDate(lead.lastContact)}</span></div>
                  {lead.assignedAt && <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Assigned</span><span className="font-medium">{formatShortDate(lead.assignedAt)}</span></div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StatusChangeModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ isOpen: false, viewHistoryOnly: false, newStatus: '' })}
        lead={lead}
        newStatus={statusModal.newStatus}
        statusHistory={lead?.statusHistory || []}
        viewHistoryOnly={statusModal.viewHistoryOnly}
        onConfirm={handleStatusChange}
      />

      <FollowUpModal
        isOpen={scheduleFollowUpModal}
        onClose={() => setScheduleFollowUpModal(false)}
        lead={lead}
        onFollowUpAdded={fetchLead}
        currentUser={getCurrentUser()}
      />

      {/* Edit Lead Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 z-[9999]">
          <div className="bg-white rounded-none sm:rounded-xl shadow-2xl max-w-2xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden">
            <LeadForm
              lead={lead}
              onSubmit={handleEditLead}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ArrowPathIcon className="h-5 w-5" /> Reschedule Follow-up
              </h3>
              <button onClick={() => setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 })} className="text-white/80 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {rescheduleModal.followUp && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{getFollowUpIcon(rescheduleModal.followUp.type)}</span>
                    <span className="font-semibold capitalize">{rescheduleModal.followUp.type}</span>
                  </div>
                  <p className="text-sm text-gray-700">{rescheduleModal.followUp.summary}</p>
                  {(rescheduleModal.followUp.nextFollowUpDate || rescheduleModal.followUp.scheduledDate) && (
                    <p className="text-xs text-gray-500 mt-2">
                      Current: {formatDate(rescheduleModal.followUp.nextFollowUpDate || rescheduleModal.followUp.scheduledDate)}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={rescheduleData.newDate}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Reschedule</label>
                <div className="space-y-2">
                  <select
                    value={rescheduleData.reasonType || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'custom') {
                        setRescheduleData(prev => ({ ...prev, reasonType: 'custom', reason: '' }));
                      } else {
                        setRescheduleData(prev => ({ ...prev, reasonType: val, reason: val }));
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select reason...</option>
                    <option value="Customer requested">Customer requested</option>
                    <option value="Not available">Not available</option>
                    <option value="Schedule conflict">Schedule conflict</option>
                    <option value="Need more preparation">Need more preparation</option>
                    <option value="Weather/Travel issues">Weather/Travel issues</option>
                    <option value="Customer not responding">Customer not responding</option>
                    <option value="Internal meeting">Internal meeting</option>
                    <option value="custom">✏️ Enter custom reason...</option>
                  </select>

                  {rescheduleData.reasonType === 'custom' && (
                    <input
                      type="text"
                      value={rescheduleData.reason}
                      onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Enter your reason..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea
                  value={rescheduleData.notes}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={rescheduleLoading || !rescheduleData.newDate}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {rescheduleLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                {rescheduleLoading ? 'Saving...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Templates Modal */}
      {whatsAppModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[9999] p-4 transition-opacity">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100 flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.45L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.588 1.977 14.128 1.988 11.48 1.988c-5.44 0-9.866 4.372-9.87 9.802 0 1.714.453 3.39 1.317 4.868l-.993 3.63 3.733-.968zM17.15 14.3c-.27-.14-1.602-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.69.88-.85 1.05-.16.17-.32.19-.59.05-2.73-1.37-3.93-2.03-5.5-4.71-.27-.45.27-.42.77-1.41.08-.16.04-.31-.02-.45-.06-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47l-.52-.01c-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.27s.98 2.62 1.11 2.8c.14.18 1.93 2.94 4.67 4.12.65.28 1.16.45 1.56.57.65.21 1.25.18 1.72.11.53-.08 1.6-.66 1.83-1.29.23-.63.23-1.18.16-1.29-.07-.11-.25-.25-.52-.39z" />
                  </svg>
                  Select Template
                </h3>
                <p className="text-xs text-emerald-50 opacity-90 mt-0.5">
                  To: <span className="font-semibold">{whatsAppModal.lead?.name}</span> ({whatsAppModal.phone})
                </p>
              </div>
              <button
                onClick={() => setWhatsAppModal({ isOpen: false, phone: '', lead: null })}
                className="text-white hover:text-emerald-100 transition-colors focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {templatesLoading ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  <span className="text-sm text-gray-500 font-medium">Loading templates...</span>
                </div>
              ) : (whatsappTemplates.length === 0 ? defaultWhatsappTemplates : whatsappTemplates).length === 0 ? (
                <div className="text-center py-8 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h4 className="text-sm font-semibold text-gray-800">No Templates Found</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    Go to <strong>Management &gt; WhatsApp Msg</strong> to create message templates first.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(whatsappTemplates.length > 0 ? whatsappTemplates : defaultWhatsappTemplates).map((template) => {
                    const previewBody = resolveMessage(template.message, whatsAppModal.lead);
                    const combinedMessage = `*${template.title}*\n\n${previewBody}`;
                    return (
                      <div
                        key={template._id}
                        onClick={() => {
                          sendWhatsAppMessage(whatsAppModal.phone, combinedMessage);
                          setWhatsAppModal({ isOpen: false, phone: '', lead: null });
                        }}
                        className="bg-white hover:bg-emerald-50/30 border border-gray-200 hover:border-emerald-300 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md group relative overflow-hidden text-left"
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-emerald-500 transition-colors"></div>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-sm font-bold text-gray-800 group-hover:text-emerald-700 transition-colors">
                            {template.title}
                          </h4>
                          {template.location && (
                            <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              📍 {template.location}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 whitespace-pre-wrap break-words bg-gray-50 group-hover:bg-white p-2.5 rounded-lg border border-gray-100 group-hover:border-emerald-100 transition-all font-mono leading-relaxed max-h-32 overflow-y-auto">
                          {combinedMessage}
                        </p>
                        <div className="mt-2 text-right">
                          <span className="text-[10px] font-bold text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-1">
                            Send Message
                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setWhatsAppModal({ isOpen: false, phone: '', lead: null })}
                className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 transition-colors shadow-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadProfile;
